import { supabase } from '../lib/supabase';
import { isNativePlatform, getPlatform } from './platform';

let registered = false;
let listeners = [];

// Persist a device push token for the signed-in user.
const saveToken = async (userId, token) => {
  if (!userId || !token) return;
  try {
    const { error } = await supabase
      .from('device_push_tokens')
      .upsert(
        {
          user_id: userId,
          token,
          platform: getPlatform(),
          enabled: true,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'token' }
      );
    if (error) console.error('Failed to save push token:', error);
  } catch (err) {
    console.error('Error saving push token:', err);
  }
};

/**
 * Register the current device for push notifications and store the token.
 * No-ops on web (or when the plugin is unavailable) so it is always safe to call.
 *
 * @param {string} userId - signed-in user id
 * @param {(notification: any) => void} [onNotification] - foreground handler
 * @param {(action: any) => void} [onAction] - tap/action handler
 */
export const initPushNotifications = async (userId, onNotification, onAction) => {
  // Guard: Android push requires Firebase. Without it, register() crashes the
  // app natively, so require an explicit opt-in flag.
  if (process.env.REACT_APP_ENABLE_PUSH !== 'true') return;
  if (!isNativePlatform() || registered) return;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import('@capacitor/push-notifications'));
  } catch (err) {
    console.warn('Push notifications plugin not available:', err);
    return;
  }

  try {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt') {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== 'granted') {
      console.log('Push permission not granted');
      return;
    }

    await PushNotifications.register();
    registered = true;

    listeners.push(
      await PushNotifications.addListener('registration', (token) => {
        saveToken(userId, token.value);
      })
    );

    listeners.push(
      await PushNotifications.addListener('registrationError', (err) => {
        console.error('Push registration error:', err);
      })
    );

    listeners.push(
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        if (onNotification) onNotification(notification);
      })
    );

    listeners.push(
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        if (onAction) onAction(action);
      })
    );
  } catch (err) {
    console.error('Error initializing push notifications:', err);
  }
};

// Disable the current device token (e.g. on sign out) and remove listeners.
export const teardownPushNotifications = async (token) => {
  await Promise.all(listeners.map((l) => l.remove?.()));
  listeners = [];
  registered = false;

  if (token) {
    try {
      await supabase
        .from('device_push_tokens')
        .update({ enabled: false, updated_at: new Date().toISOString() })
        .eq('token', token);
    } catch (err) {
      console.error('Error disabling push token:', err);
    }
  }
};
